import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  DiscrepancyListResponse,
  DiscrepancyReport,
  DiscrepancyStats,
  DiscrepancyWrite,
  GeneratePdfResponse,
} from './types'

const ROOT = '/api/inventory-discrepancy-reports'

export interface UseDiscrepanciesOptions {
  status?: string | null
  search?: string
  page?: number
  limit?: number
}

export function useDiscrepancies(opts: UseDiscrepanciesOptions = {}) {
  const { status, search, page = 1, limit = 50 } = opts
  return useQuery({
    queryKey: ['inventory-discrepancies', 'list', status, search, page, limit],
    queryFn: () => {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (search) params.set('search', search)
      params.set('page', String(page))
      params.set('limit', String(limit))
      return jsonFetch<DiscrepancyListResponse>(`${ROOT}?${params}`)
    },
    staleTime: 15_000,
  })
}

export function useDiscrepancyStats() {
  return useQuery({
    queryKey: ['inventory-discrepancies', 'stats'],
    queryFn: () => jsonFetch<DiscrepancyStats>(`${ROOT}/stats`),
    staleTime: 30_000,
  })
}

export function useDiscrepancy(id: string | null) {
  return useQuery({
    queryKey: ['inventory-discrepancies', 'detail', id],
    queryFn: () => jsonFetch<DiscrepancyReport>(`${ROOT}/${id}`),
    enabled: Boolean(id),
    staleTime: 15_000,
  })
}

export function useCreateDiscrepancy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<DiscrepancyWrite>) =>
      jsonFetch<DiscrepancyReport>(ROOT, { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory-discrepancies'] })
    },
  })
}

export function useUpdateDiscrepancy(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      jsonFetch<DiscrepancyReport>(`${ROOT}/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory-discrepancies'] })
    },
  })
}

export function useGenerateDiscrepancyPdf(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      jsonFetch<GeneratePdfResponse>(`${ROOT}/${id}/generate-pdf`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory-discrepancies', 'detail', id] })
    },
  })
}

export function useLinkIncidentReport(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (warehouseIncidentId: string) =>
      jsonFetch<DiscrepancyReport>(`${ROOT}/${id}/link`, {
        method: 'POST',
        body: { warehouse_incident_id: warehouseIncidentId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory-discrepancies'] })
      void qc.invalidateQueries({ queryKey: ['warehouse-incidents'] })
    },
  })
}

export function useUnlinkIncidentReport(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => jsonFetch<DiscrepancyReport>(`${ROOT}/${id}/unlink`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory-discrepancies'] })
      void qc.invalidateQueries({ queryKey: ['warehouse-incidents'] })
    },
  })
}

export function useDeleteDiscrepancy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ deleted: string }>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory-discrepancies'] })
    },
  })
}
